import { HttpErrorResponse } from '@angular/common/http';

import type { Pageable, ResponseError } from '@kp/interfaces';

function formatErrorCauseWithBussinessError(
  error: unknown,
): string | undefined {
  return Error.isError(error) && isBusinessError(error.cause)
    ? error.cause.businessErrors.map(err => err.message).join('<br>')
    : undefined;
}

function isBusinessError(error: unknown): error is ResponseError {
  return Array.isArray((error as ResponseError | undefined)?.businessErrors);
}

function isBusinessErrorResponse(error: unknown): ResponseError | undefined {
  return error instanceof HttpErrorResponse && isBusinessError(error.error)
    ? error.error
    : undefined;
}

function isHttpErrorResponse(error: unknown): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse;
}

function isNullOrUndefined(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

function isTruthy(value: unknown, msg: string): asserts value {
  if (!value) {
    throw new Error(msg);
  }
}

const unwrap = <T>(variable: T | null | undefined, errorMsg?: string): T => {
  if (variable === undefined || variable === null) {
    throw new Error(errorMsg || 'Variable is undefined');
  }

  return variable;
};

type BankAccount = BankAccountWithoutPrefix | BankAccountWithPrefix;
type BankAccountWithoutPrefix = {
  account: string;
  bankCode: string;
};

type BankAccountWithPrefix = {
  account: string;
  bankCode: string;
  prefix: string;
};

type RgbColor = {
  b: number;
  g: number;
  r: number;
};

function assertBankAccountWithPrefix(
  value: unknown,
): asserts value is BankAccountWithPrefix {
  const check =
    typeof value === 'object' &&
    value !== null &&
    'account' in value &&
    'prefix' in value &&
    'bankCode' in value;

  if (!check) {
    throw new Error('Value is not BankAccountWithPrefix');
  }
}

function assertBlob(body: unknown): asserts body is Blob {
  if (!(body instanceof Blob)) {
    throw new TypeError('Expected a Blob');
  }
}

/**
 *@throws an error if given value is not a number
 */
function assertNumber(
  value: unknown,
  msg = 'Value is not string',
): asserts value is string {
  if (typeof value !== 'number') {
    throw new TypeError(msg);
  }
}
/**
 *@throws an error if given value is not a string
 */
function assertString(
  value: unknown,
  msg = 'Value is not string',
): asserts value is string {
  if (typeof value !== 'string') {
    throw new TypeError(msg);
  }
}
/**
 *@throws an error if given value is not an array of strings
 */
function assertStringArray(
  value: Array<unknown>,
): asserts value is Array<string> {
  if (value.some(v => typeof v !== 'string')) {
    throw new TypeError('Value is not string');
  }
}

/**
 *@throws an error if given value is null or undefined
 */
function assertValue<T>(
  value: T,
  msg = 'Value is null or undefined',
): asserts value is NonNullable<T> {
  // oxlint-disable-next-line no-eq-null, eqeqeq
  if (value == null) {
    throw new Error(msg);
  }
}
/**
 * Returns enumeration of item and index. Index can be adjusted by same value.
 * @yields
 */
function* enumerate<T>(
  iterable: Array<T>,
  defaultValue = 0,
): IterableIterator<[number, T]> {
  let i = defaultValue;

  for (const x of iterable) {
    yield [i, x];
    i += 1;
  }
}

function hexToRgb(hex: string): RgbColor | null {
  const value = hex.replace(/^#/u, '');

  if (!/^(?:[\da-f]{3}|[\da-f]{6})$/iu.test(value)) {
    return null;
  }

  const normalized =
    value.length === 3
      ? value.replaceAll(/./gu, character => character.repeat(2))
      : value;

  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
  };
}

/**
 * Checks whether is object of Pageable type
 */
function isPageable<U>(object: unknown): object is Pageable<U> {
  return (
    object !== null &&
    typeof object === 'object' &&
    'content' in object &&
    'totalElements' in object &&
    'pageable' in object
  );
}

/**
 * Returns boolean if given value is not an array of strings
 */
function isStringArray(value: Array<unknown>): value is Array<string> {
  return value.every(v => typeof v === 'string');
}

// oxlint-disable-next-line max-params
function lightText(r: number, g: number, b: number, a = 100): boolean {
  // prettier-ignore
  return (r * 0.299) + (g * 0.587) + (b * 0.114) + ((1 - a) * 255) > 186;
}

function padAccountNumber(account?: string): string {
  return (account || '').padStart(10, '0');
}

function padBankAccountString(input: string, withBankCode = true): string {
  if (!input) {
    return input;
  }

  const [account, bankCode] = input.split('/', 2);
  const [prefix, acc] =
    (account?.length ?? 0) <= 10 && !account?.includes('-')
      ? ['', account]
      : (account || '').split('-');
  const bank = withBankCode ? `/${bankCode}` : '';

  return `${account?.length === 16 ? account : (prefix || '').padStart(6, '0') + (acc || '').padStart(10, '0')}${bank}`;
}

function padBankAccountStringWithCode(input: string): {
  accountNumber: string;
  bankCode: string;
} {
  const [accountNumber = '', bankCode = ''] = padBankAccountString(input).split(
    '/',
    2,
  );

  return { accountNumber, bankCode };
}

function padPrefix(prefix?: string): string {
  return (prefix || '').padStart(6, '0');
}

function rgbToHex(r: number, g: number, b: number): string {
  // oxlint-disable-next-line no-bitwise
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

// https://stackoverflow.com/questions/7342957/how-do-you-round-to-1-decimal-place-in-javascript
function round(value: number, precision: number): number {
  const multiplier = 10 ** (precision || 0);

  return Math.round(value * multiplier) / multiplier;
}

/**
 * Converts the input value to a boolean.
 *
 * If the input value is of type boolean, it returns it unchanged.
 * If the input value is a string, it converts it to lowercase
 * and checks if it's equal to "false"; if so, it returns `false`, otherwise `true`.
 * For all other values, it uses the default boolean conversion.
 * @param value - The input value to convert to boolean.
 * @returns The converted value as a boolean.
 * @example
 * const result1 = toBoolean(true);    // true
 * const result2 = toBoolean("true");   // true
 * const result3 = toBoolean("false");  // false
 * const result4 = toBoolean(1);        // true
 * const result5 = toBoolean(0);        // false
 */
function toBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  return typeof value === 'string' ? value.toLowerCase() !== 'false' : !!value;
}

function unwrapOr<T>(variable: T | undefined, or?: unknown): T;
function unwrapOr<U>(variable: undefined, or: U): U;
function unwrapOr<T, U>(variable: T | undefined, or: U): T | U {
  return variable === undefined ? or : variable;
}

/**
 * Checks whether a defined number is greater than the comparison value.
 *
 * @param value - Number to check, or `undefined`.
 * @param comparison - Value to compare against. Defaults to `0`.
 * @returns `true` when `value` is defined and greater than `comparison`;
 * otherwise, `false`.
 */
const isGreaterThan = (value: number | undefined, comparison = 0): boolean =>
  value !== undefined && value > comparison;

export type { BankAccount, BankAccountWithoutPrefix, BankAccountWithPrefix };
export {
  assertBankAccountWithPrefix,
  assertBlob,
  assertNumber,
  assertString,
  assertStringArray,
  assertValue,
  enumerate,
  formatErrorCauseWithBussinessError,
  hexToRgb,
  isBusinessError,
  isBusinessErrorResponse,
  isGreaterThan,
  isHttpErrorResponse,
  isNullOrUndefined,
  isPageable,
  isStringArray,
  isTruthy,
  lightText,
  padAccountNumber,
  padBankAccountString,
  padBankAccountStringWithCode,
  padPrefix,
  rgbToHex,
  round,
  toBoolean,
  unwrap,
  unwrapOr,
};
